/* eslint-disable arrow-parens */
import { useEffect, useState, Suspense, lazy, useRef } from 'react'
import { Auth } from 'aws-amplify'
import { useDispatch, useSelector } from 'react-redux'
import { Switch, Redirect, Route, useLocation } from 'react-router-dom'
import { GuardProvider, GuardedRoute } from 'react-router-guards'
import { Loader } from '@/components'
import { SET_CURRENT_USER } from '@/store/auth/authTypes'
import { DashboardLayout } from './views/Dashboard/Layout'
import { APIS } from './utils/apiEndpoints'
import { clearStorage, getItemStorage, setItemStorage } from './utils/storage'
import constants from './utils/constants'
import common from './utils/common'

const AuthPage = lazy(() => import('@/views/Auth/AuthPage'))
const Profile = lazy(() => import('@/views/Profile'))
const Dashboard = lazy(() => import('@/views/Dashboard'))
const AdminDashboard = lazy(() => import('@/views/Dashboard/AdminRoutes'))
const CheckoutPage = lazy(() => import('@/views/CheckoutPage'))
const StripeOAuth = lazy(() => import('@/views/StripeOAuth'))
const Chat = lazy(() => import('@/views/Chat'))
const PageNotFound = lazy(() => import('@/views/404'))
const SaveCardSuccess = lazy(() => import('@/views/CheckoutPage/SaveCardSuccess'))
const Settings = lazy(() => import('@/views/Settings'))
const Callback = lazy(() => import('@/views/Callback'))
const Billing = lazy(() => import('@/views/Billing'))
const ViewTripNew = lazy(() => import('@/views/ViewTrip'))
const UpdateTrip = lazy(() => import('@/views/UpdateTrip'))
const ActivityLog = lazy(() => import('@/views/Dashboard/ActivityLog'))

const PageTransition = ({ children }) => {
  const location = useLocation()
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.animation = 'none'
    void el.offsetHeight
    el.style.animation = 'pageFadeIn 0.3s ease'
  }, [location.pathname])

  return (
    <div ref={ref} style={{ animation: 'pageFadeIn 0.3s ease' }}>
      {children}
    </div>
  )
}

const MainRoutes = () => (
  <Suspense fallback={<Loader loading={true} pageLevel />}>
    <PageTransition>
    <Switch>
      {/* <GuardedRoute
        path="/dashboard/organizations"
        name="Dashboard"
        component={<Redirect from="/dashboard/organizations" to="/dashboard" />}
      /> */}
      <GuardedRoute
        path="/login"
        name="Login"
        component={AuthPage}
        meta={{ auth: false }}
      />
      <GuardedRoute
        path="/logout"
        name="Login"
        component={AuthPage}
        meta={{ auth: false }}
      />
      <GuardedRoute
        path="/signup"
        name="Signup"
        component={AuthPage}
        meta={{ auth: false }}
      />
      <GuardedRoute
        path="/callback"
        name="Callback"
        component={Callback}
        meta={{ auth: true }}
      />
      <GuardedRoute
        path="/profile/:userId"
        name="Profile"
        component={Profile}
      />
      <GuardedRoute
        path="/settings"
        name="AccountSetting"
        component={Settings}
        meta={{ auth: true }}
      />
      <GuardedRoute
        path="/billing"
        name="BillingDash"
        component={Billing}
        meta={{ auth: true }}
      />
      <GuardedRoute
        path="/admin"
        name="Admin"
        component={AdminDashboard}
        meta={{ auth: true, admin: true }}
      />
      <GuardedRoute
        path="/event/:tripId/edit"
        name="EditTrip"
        component={UpdateTrip}
        meta={{ auth: true, admin: true }}
      />
      <GuardedRoute
        path="/event/:tripId/book"
        name="BookTrip"
        component={CheckoutPage}
      />
      <GuardedRoute
        path="/event/:tripId"
        name="ViewTrip"
        component={ViewTripNew}
      />
      <GuardedRoute
        path="/save-card-success"
        name="SaveCardSuccess"
        component={SaveCardSuccess}
        meta={{ auth: true }}
      />
      <GuardedRoute
        path="/connect/oauth"
        name="VerifyStripeConnect"
        component={StripeOAuth}
        meta={{ auth: true }}
      />
      <GuardedRoute
        path="/messages"
        name="Messages"
        component={Chat}
        meta={{ auth: true }}
      />
      <GuardedRoute
        path="/activity-logs"
        name="ActivityLogs"
        component={ActivityLog}
        meta={{ auth: true }}
      />
      <GuardedRoute
        path="/dashboard"
        name="Dashboard"
        component={Dashboard}
      />
      <Route path="/">
        <Redirect to="/dashboard" />
      </Route>
    </Switch>
    </PageTransition>
  </Suspense>
)

const MainLayout = () => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const { currentUser } = useSelector((state) => state.auth)

  const { trip } = useSelector((state) => state.activeTrips);

  const requireLogin = async (to, _from, next) => {
    const isAuthRequired = to.meta.auth;
    const isUserLoggedIn = !!currentUser?.username;
    const isCallbackPage = to.location.pathname.includes('/callback');
    const isPublicPage =
      to.location.pathname.includes('/event') && !to.location.pathname.includes('/edit') ||
      to.location.pathname.includes('/profile') || to.location.pathname.includes('/dashboard/organizations/');

    const profileMe = to.location.pathname.includes('/profile/me');

    if (profileMe && !isUserLoggedIn) {
      return next.redirect('/login')
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (isAuthRequired) {
      return isUserLoggedIn ? next() : next.redirect('/login');
    }

    if (isUserLoggedIn) {

      if (isCallbackPage) {
        const redirectUrl = getItemStorage('redirectUrl');
        if (redirectUrl) {
          clearStorage('redirectUrl');
          return next.redirect(redirectUrl);
        }
        return next.redirect('/dashboard');
      }

      if (isPublicPage) {
        return next();
      }

      return next.redirect('/dashboard');
    }

    return next();
  };

  const fetchUserSession = async () => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setLoading(true)
      const user = await Auth.currentUserInfo();
      if (!user) throw 'Logged out'
      const userResult = await APIS.getCurrentUser();
      const userInfo = {
        ...(userResult?.result || {}),
        id: user.id,
        isEmailVerified: user.attributes.email_verified,
        newEmail: user.attributes.email,
        cognitoUsername: user?.username,
      }
      if (user?.username && user?.username?.includes('Google_'))
        userInfo['identityProvider'] = 'google'
      setItemStorage(constants.CURRENT_USER_STORAGE, userInfo)
      dispatch({ type: SET_CURRENT_USER, payload: userInfo })
    } catch (err) {
      dispatch({ type: SET_CURRENT_USER, payload: {} })
      if (err?.response?.data?.message?.includes('Signature expired')) {
        const signOutUrl = `${process.env.REACT_APP_HOST_PATH}/logout`; // Or whichever URL you configured in Cognito's Allowed Sign-Out URLs
        await Auth.signOut({
            // --- FIX IS HERE ---
            global: true, // Optional: Sign out from all devices
            redirectUri: signOutUrl // CRITICAL: Tells Cognito where to send the user
            // --- END OF FIX ---
        });
        return common.errorToast(
          "We're sorry, but it appears that your login attempt has failed. In order to resolve this issue, we kindly request you to set your system's time zone to update automatically. Once you've made this adjustment, please try logging in again. If you continue to experience any difficulties, please don't hesitate to reach out to our support team for further assistance.",
        )
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUserSession()
  }, [])

  return (
    <Loader loading={loading}>
      <DashboardLayout>
        {!loading && (
          <GuardProvider guards={[requireLogin]} error={PageNotFound}>
            <MainRoutes />
          </GuardProvider>
        )}
      </DashboardLayout>
    </Loader>
  )
}

export default MainLayout
